import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/brand";
import { Users, UserPlus, Trash2, AlertCircle, CheckCircle2, MapPin, Mail } from "lucide-react";

export default function UsersManager() {
  const [tab, setTab] = useState("coach");
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "coach", phone: "", state: "", district: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(
    () => api.get("/admin/users", { params: { role: tab } }).then((r) => setItems(r.data)),
    [tab]
  );
  useEffect(() => { refresh(); }, [refresh]);

  const create = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setLoading(true);
    try {
      await api.post("/admin/users", { ...form, role: tab });
      setForm({ email: "", password: "", name: "", role: tab, phone: "", state: "", district: "" });
      setOk(`${tab === "coach" ? "Coach" : "Official"} added.`);
      setShowForm(false);
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); refresh(); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="users-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-nkf-red" />
          <h2 className="font-display text-xl text-nkf-blue">Coaches & Officials</h2>
        </div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="users-add-toggle" className="inline-flex items-center gap-1.5 bg-nkf-blue hover:bg-nkf-navy text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded transition">
          <UserPlus className="h-3.5 w-3.5" /> {showForm ? "Cancel" : `Add ${tab === "coach" ? "Coach" : "Official"}`}
        </button>
      </div>

      <div className="mt-4 inline-flex bg-slate-100 rounded-md p-1">
        {["coach", "official"].map((r) => (
          <button key={r} onClick={() => setTab(r)} data-testid={`users-tab-${r}`} className={`px-4 py-1.5 text-xs uppercase tracking-widest font-semibold rounded transition ${tab === r ? "bg-white text-nkf-blue shadow-sm" : "text-slate-500"}`}>
            {r === "coach" ? "Academy / Coach" : "Officials"}
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={create} className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3 p-4 bg-slate-50 border border-slate-200 rounded-md">
          <Input wide="md:col-span-6" label="Full Name *" value={form.name} set={(v) => set("name", v)} required testid="user-name" />
          <Input wide="md:col-span-6" label="Email *" type="email" value={form.email} set={(v) => set("email", v)} required testid="user-email" />
          <Input wide="md:col-span-6" label="Temporary Password *" type="text" value={form.password} set={(v) => set("password", v)} required testid="user-password" placeholder="At least 8 characters" />
          <Input wide="md:col-span-6" label="Phone" value={form.phone} set={(v) => set("phone", v)} testid="user-phone" />
          <label className="md:col-span-6 block" data-testid="user-state">
            <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">State</span>
            <select value={form.state} onChange={(e) => set("state", e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">Select state</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Input wide="md:col-span-6" label="District" value={form.district} set={(v) => set("district", v)} testid="user-district" />
          <div className="md:col-span-12 flex justify-end">
            <button disabled={loading} data-testid="user-create" className="bg-nkf-blue text-white px-5 py-2.5 rounded text-sm font-bold uppercase tracking-widest hover:bg-nkf-navy transition disabled:opacity-60">
              {loading ? "Saving…" : `Create ${tab === "coach" ? "Coach" : "Official"}`}
            </button>
          </div>
        </form>
      )}

      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
      {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((u) => (
          <div key={u.id} className="border border-slate-200 rounded-md p-4 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
            <div className="h-11 w-11 rounded-full bg-nkf-blue/10 flex items-center justify-center text-nkf-blue font-display">{(u.name || "?").slice(0, 1)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-nkf-blue truncate">{u.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {u.state || "—"}</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{u.membership_no}</div>
            </div>
            <button onClick={() => remove(u.id, u.name)} className="text-red-600 hover:text-red-800 transition p-2" aria-label="Remove" data-testid={`user-del-${u.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!items.length && <div className="md:col-span-2 text-center text-slate-400 text-sm py-6 border border-dashed rounded-md">No {tab === "coach" ? "coaches" : "officials"} yet. Click &ldquo;Add&rdquo; to create one.</div>}
      </div>
    </section>
  );
}

function Input({ wide, label, value, set, required, type = "text", placeholder, testid }) {
  return (
    <label className={`block ${wide || ""}`} data-testid={testid}>
      <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">{label}</span>
      <input required={required} type={type} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
    </label>
  );
}
